import React, { useState, useEffect } from "react";
import NavBar from "../layout/NavBar";
import Footer from "../layout/Footer";
import NovoEventoService from "../../services/Eventos/NovoEventoService";
import SideBarDetailCorrida from "./SideBarDetailCorrida";
import InscricaoCorredor from "./InscricaoCorredor";
import { formatDateBR } from "../../utils/dateUtils";
import { obterLoteAtivoEDisponivel } from "../../utils/loteUtils";
import { Sidebar, Modal, Button, Badge, Spinner } from "../ui";
import { MapPin, Eye, ChevronLeft, ChevronRight, Map } from "lucide-react";

function NovasCorridas() {
    const [corridas, setCorridas] = useState([]);
    const [loading, setLoading] = useState(true);
    const [selectedCorrida, setSelectedCorrida] = useState(null);
    const [visibleSidebar, setVisibleSidebar] = useState(false);
    const [visibleDialog, setVisibleDialog] = useState(false);
    const [categorias, setCategorias] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const rowsPerPage = 5;

    const eventoService = new NovoEventoService();

    useEffect(() => {
        eventoService.getTodosEventos()
            .then(res => res.json())
            .then(data => {
                setCorridas(Array.isArray(data) ? data : []);
                setLoading(false);
            })
            .catch(err => {
                console.error("Erro ao carregar eventos:", err);
                setLoading(false);
            });
    }, []);

    const detailCorrida = (corrida) => {
        setSelectedCorrida(corrida);
        setVisibleSidebar(true);
    };

    const dialogInscricao = async (corrida) => {
        try {
            const res = await eventoService.geCategoriaPorCorrida(corrida.id);
            const cats = await res.json();
            setCategorias(Array.isArray(cats) ? cats : []);
        } catch (err) {
            console.error("Erro ao buscar categorias:", err);
        }
        setSelectedCorrida(corrida);
        setVisibleSidebar(false);
        setVisibleDialog(true);
    };

    const getBadgeVariant = (status) => {
        switch (status) {
            case 'Em Preparação':
                return 'info';
            case 'Inscrição Aberta':
                return 'success';
            case 'Inscrição Encerrada':
                return 'warning';
            case 'Cancelado':
                return 'danger';
            case 'Finalizado':
                return 'secondary';
            default:
                return 'secondary';
        }
    };

    // Paginação
    const totalPages = Math.ceil(corridas.length / rowsPerPage) || 1;
    const startIndex = (currentPage - 1) * rowsPerPage;
    const paginatedCorridas = corridas.slice(startIndex, startIndex + rowsPerPage);

    return (
        <div className="min-h-screen flex flex-col justify-between bg-urb-dark text-white">
            <NavBar />

            <main className="flex-grow max-w-7xl mx-auto w-full px-4 sm:px-6 lg:px-8 py-8">
                <div className="mb-6 flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div>
                        <h1 className="text-2xl sm:text-3xl font-extrabold text-white">Próximos Eventos</h1>
                        <p className="text-slate-400 text-sm mt-1">
                            Descubra e participe dos melhores eventos esportivos disponíveis.
                        </p>
                    </div>
                </div>

                {loading ? (
                    <div className="flex flex-col items-center justify-center py-20 gap-3 min-h-[45vh]">
                        <Spinner size="lg" />
                        <span className="text-sm text-slate-400">Carregando corridas...</span>
                    </div>
                ) : corridas.length === 0 ? (
                    <div className="bg-urb-card border border-urb-border rounded-xl p-12 text-center">
                        <p className="text-slate-400 text-base">Nenhuma corrida encontrada no momento.</p>
                    </div>
                ) : (
                    <div className="bg-urb-card border border-urb-border rounded-2xl shadow-xl overflow-hidden divide-y divide-urb-border">
                        {paginatedCorridas.map((product) => {
                            const loteAtivo = (Array.isArray(product.lotes) ? obterLoteAtivoEDisponivel(product.lotes) : null) || product.lote_ativo || null;
                            const valorDisplay = loteAtivo
                                ? loteAtivo.valor
                                : product.valor_inscricao;

                            return (
                                <div
                                    key={product.id}
                                    className="p-5 sm:p-6 flex flex-col xl:flex-row items-center xl:items-start gap-6 hover:bg-slate-900/40 transition-colors"
                                >
                                    <img
                                        src={product.folder || "https://images.unsplash.com/photo-1476480862126-209bfaa8edc8?auto=format&fit=crop&w=800&q=80"}
                                        alt={product.nome_corrida}
                                        className="w-full sm:w-64 xl:w-48 h-40 object-cover rounded-xl border border-urb-border shrink-0 shadow-md"
                                    />

                                    <div className="flex flex-col sm:flex-row justify-between items-center sm:items-start xl:items-center flex-1 gap-6 w-full">
                                        <div className="flex flex-col items-center sm:items-start gap-2.5 text-center sm:text-left">
                                            <h2 className="text-xl sm:text-2xl font-bold text-white">
                                                {product.nome_corrida}
                                            </h2>

                                            <div className="flex flex-wrap items-center justify-center sm:justify-start gap-2">
                                                <Badge variant={getBadgeVariant(product.status_evento)}>
                                                    {product.status_evento}
                                                </Badge>
                                                {product.is_gratuita && (
                                                    <Badge variant="success" className="font-bold">
                                                        Gratuita
                                                    </Badge>
                                                )}
                                                <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-slate-800 text-slate-300 border border-urb-border">
                                                    <MapPin className="w-3.5 h-3.5 text-urb-primary" />
                                                    {product.cidade_evento}{product.uf ? ` - ${product.uf}` : ''}
                                                </span>
                                                {product.trajeto_mapa && (
                                                    <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-medium bg-emerald-950/60 text-emerald-300 border border-emerald-800/40">
                                                        <Map className="w-3.5 h-3.5 text-emerald-400" />
                                                        {product.trajeto_mapa.distancia_km ? `${product.trajeto_mapa.distancia_km} km no Mapa` : 'Trajeto no Mapa'}
                                                    </span>
                                                )}
                                            </div>

                                            <div className="text-xs text-slate-400">
                                                {product.status_evento === 'Em Preparação' && product.inicio_inscricao && (
                                                    <span>Início das inscrições: <strong className="text-slate-300">{formatDateBR(product.inicio_inscricao)}</strong></span>
                                                )}
                                                {product.status_evento === 'Inscrição Aberta' && product.fim_inscricao && (
                                                    <span>Fim das inscrições: <strong className="text-slate-300">{formatDateBR(product.fim_inscricao)}</strong></span>
                                                )}
                                            </div>
                                        </div>

                                        <div className="flex flex-col sm:items-end items-center gap-3 shrink-0">
                                            {product.is_gratuita ? (
                                                <Badge variant="success" className="text-base font-bold px-3 py-1">
                                                    Gratuita
                                                </Badge>
                                            ) : (
                                                <div className="text-center sm:text-right">
                                                    {loteAtivo && (
                                                        <span className="text-xs text-slate-400 block font-medium">
                                                            {loteAtivo.nome_lote}
                                                        </span>
                                                    )}
                                                    <span className="text-2xl font-extrabold text-white">
                                                        {valorDisplay
                                                            ? `R$ ${Number(valorDisplay).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
                                                            : '—'
                                                        }
                                                    </span>
                                                </div>
                                            )}

                                            <Button
                                                variant="secondary"
                                                onClick={() => detailCorrida(product)}
                                                className="flex items-center gap-2 border-urb-primary/40 text-urb-primary hover:bg-urb-primary/10 rounded-full px-5 py-2 text-sm font-semibold"
                                            >
                                                <Eye className="w-4 h-4" />
                                                Visualizar
                                            </Button>
                                        </div>
                                    </div>
                                </div>
                            );
                        })}

                        {/* Paginação */}
                        {totalPages > 1 && (
                            <div className="p-4 flex items-center justify-between bg-urb-card-light/40">
                                <span className="text-xs text-slate-400">
                                    Página {currentPage} de {totalPages} ({corridas.length} corridas)
                                </span>
                                <div className="flex items-center gap-2">
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled={currentPage === 1}
                                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                                        className="p-1.5"
                                    >
                                        <ChevronLeft className="w-4 h-4" />
                                    </Button>
                                    <Button
                                        variant="ghost"
                                        size="sm"
                                        disabled={currentPage === totalPages}
                                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                                        className="p-1.5"
                                    >
                                        <ChevronRight className="w-4 h-4" />
                                    </Button>
                                </div>
                            </div>
                        )}
                    </div>
                )}

                {/* Sidebar com Detalhes da Corrida */}
                <Sidebar
                    visible={visibleSidebar}
                    onHide={() => setVisibleSidebar(false)}
                    position="right"
                    className="w-full max-w-full lg:w-screen lg:max-w-none"
                >
                    <SideBarDetailCorrida
                        produto={selectedCorrida}
                        onInscricao={() => dialogInscricao(selectedCorrida)}
                    />
                </Sidebar>

                {/* Modal de Inscrição */}
                <Modal
                    isOpen={visibleDialog}
                    onClose={() => setVisibleDialog(false)}
                    title="Inscrição na Corrida"
                    maxWidth="max-w-4xl"
                >
                    {selectedCorrida && (
                        <InscricaoCorredor
                            produto={selectedCorrida}
                            categorias={categorias}
                        />
                    )}
                </Modal>
            </main>

            <Footer />
        </div>
    );
}

export default NovasCorridas;